<div class="p-6 space-y-8 animate-in fade-in duration-700">
  <!-- Header Section -->
  <div class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between bg-base-100/40 backdrop-blur-xl p-6 rounded-3xl border border-white/10 shadow-2xl">
    <div>
      <h1 class="text-3xl font-black bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Rider Fleet</h1>
      <p class="text-sm font-medium opacity-60 mt-1">Manage, monitor and scale your delivery operations</p>
    </div>

    <div class="flex flex-col sm:flex-row gap-4 items-center">
      <div class="relative w-full sm:w-80 group">
        <i class="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 opacity-30 group-focus-within:text-primary group-focus-within:opacity-100 transition-all"></i>
        <input
          type="text"
          [ngModel]="searchQuery()"
          (ngModelChange)="onSearchChange($event)"
          placeholder="Search by name or rider code..."
          class="input input-ghost bg-base-200/50 hover:bg-base-200 focus:bg-base-100 border-none rounded-2xl w-full pl-12 h-12 transition-all shadow-inner font-medium text-sm"
        />
      </div>
    </div>
  </div>

  <!-- Filters & Stats Bar -->
  <div class="flex flex-col md:flex-row gap-4 items-center justify-between">
    <div class="flex flex-wrap gap-2 p-1.5 bg-base-200/30 backdrop-blur-md rounded-2xl border border-white/5">
      @for (tab of statusTabs; track tab) {
        <button
          class="btn btn-sm rounded-xl px-4 normal-case border-none transition-all"
          [class.bg-primary]="activeStatus === tab"
          [class.text-primary-content]="activeStatus === tab"
          [class.shadow-lg]="activeStatus === tab"
          [class.shadow-primary/20]="activeStatus === tab"
          [class.bg-transparent]="activeStatus !== tab"
          [class.hover:bg-base-200]="activeStatus !== tab"
          (click)="changeStatus(tab)"
        >
          {{ tab === 'PENDING' ? 'Pending Verification' : tab }}
          @if (tab === 'PENDING' && pendingRiderCount > 0) {
            <span class="badge badge-xs badge-error ml-1"></span>
          }
        </button>
      }
    </div>

    <div class="flex items-center gap-6 opacity-60 text-xs font-bold uppercase tracking-widest px-4">
      <div class="flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-success"></span>
        <span>{{ onlineRiderCount }} Active</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-warning"></span>
        <span>{{ pendingRiderCount }} Pending</span>
      </div>
    </div>
  </div>

  <!-- Main Rider Table -->
  <div class="overflow-hidden rounded-[2rem] border border-white/5 bg-base-100/40 backdrop-blur-2xl shadow-2xl">
    <div class="overflow-x-auto">
      <table class="table table-lg w-full">
        <thead>
          <tr class="border-b border-white/5 bg-base-200/20">
            <th class="text-[10px] font-black uppercase tracking-widest opacity-40 py-6">Rider Profile</th>
            <th class="text-[10px] font-black uppercase tracking-widest opacity-40 py-6">Operational Status</th>
            <th class="text-[10px] font-black uppercase tracking-widest opacity-40 py-6">Vehicle & Type</th>
            <th class="text-[10px] font-black uppercase tracking-widest opacity-40 py-6 text-center">Reviews</th>
            <th class="text-[10px] font-black uppercase tracking-widest opacity-40 py-6">Earning Analytics</th>
            <th class="text-[10px] font-black uppercase tracking-widest opacity-40 py-6 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-white/5">
          @if (isLoading()) {
            @for (row of [1,2,3,4,5]; track row) {
              <tr class="animate-pulse">
                <td colspan="6" class="p-8">
                  <div class="flex items-center gap-4">
                    <div class="w-14 h-14 bg-base-300/50 rounded-2xl"></div>
                    <div class="space-y-2">
                      <div class="h-4 bg-base-300/50 rounded w-48"></div>
                      <div class="h-3 bg-base-300/50 rounded w-24"></div>
                    </div>
                  </div>
                </td>
              </tr>
            }
          } @else if (paginatedRiders.length > 0) {
            @for (rider of paginatedRiders; track rider.id) {
              <tr class="group hover:bg-white/5 transition-all">
                <!-- Rider Profile -->
                <td class="py-6">
                  <div class="flex items-center gap-4">
                    <div class="avatar relative group-hover:scale-105 transition-transform">
                      <div class="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center border border-white/10 shadow-xl overflow-hidden">
                        @if (rider.profile_photo_url) {
                          <img [src]="rider.profile_photo_url" alt="Profile" class="w-full h-full object-cover">
                        } @else {
                          <span class="text-xl font-black text-primary">{{ rider.full_name[0] }}</span>
                        }
                      </div>
                      @if (rider.presence?.is_online) {
                        <span class="absolute -bottom-1 -right-1 w-4 h-4 rounded-lg bg-success border-4 border-base-100 shadow-lg animate-pulse"></span>
                      }
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-center gap-2">
                        <p class="font-black text-base truncate">{{ rider.full_name }}</p>
                        @if ((rider.verification_status || '').toLowerCase() === 'approved') {
                          <i class="fa-solid fa-certificate text-primary text-xs" title="Verified Professional"></i>
                        }
                      </div>
                      <p class="text-[10px] font-mono opacity-40 tracking-wider uppercase">PID: {{ rider.code || rider.id.slice(0,8) }}</p>
                    </div>
                  </div>
                </td>

                <!-- Operational Status -->
                <td>
                  <div class="flex flex-col gap-1">
                    @let currentStatus = rider.presence?.status || rider.status;
                    <span
                      class="badge badge-sm font-black text-[9px] uppercase tracking-wider py-2.5 px-3 rounded-lg border-none"
                      [ngClass]="{
                        'bg-success/10 text-success': currentStatus === 'Online',
                        'bg-error/10 text-error': currentStatus === 'Offline',
                        'bg-warning/10 text-warning': currentStatus === 'Busy'
                      }"
                    >
                      <span class="w-1 h-1 rounded-full bg-current mr-1.5"></span>
                      {{ currentStatus || 'Offline' }}
                    </span>
                    @if (rider.presence?.last_seen_at) {
                      <span class="text-[9px] opacity-40 font-bold ml-1">last active {{ rider.presence?.last_seen_at | date:'shortTime' }}</span>
                    }
                  </div>
                </td>

                <!-- Vehicle -->
                <td>
                  <div class="flex items-center gap-3">
                    <div class="p-2.5 rounded-xl bg-base-200/50 border border-white/5">
                      <i class="fa-solid" [ngClass]="getVehicleIcon(rider.vehicle_type)"></i>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-xs font-bold text-base-content/80">{{ rider.vehicle_type || 'Standard' }}</span>
                      <span class="text-[9px] opacity-40 font-black uppercase tracking-tighter">Logistics Class</span>
                    </div>
                  </div>
                </td>

                <!-- Reviews -->
                <td>
                  <div class="flex flex-col items-center justify-center">
                    <div class="flex items-center gap-1.5">
                      <span class="text-lg font-black">{{ rider.rating || '0.0' }}</span>
                      <i class="fa-solid fa-star text-[10px] text-warning mb-0.5"></i>
                    </div>
                    <div class="w-16 h-1 bg-base-200 rounded-full mt-1.5 overflow-hidden">
                      <div class="h-full bg-warning" [style.width.%]="((rider.rating || 0) / 5) * 100"></div>
                    </div>
                  </div>
                </td>

                <!-- Earnings -->
                <td>
                  <div class="flex flex-col">
                    <div class="flex items-center gap-1">
                      <span class="text-sm font-black text-success">₹{{ (rider.wallet?.balance || 0) | number:'1.0-2' }}</span>
                      <span class="text-[9px] italic opacity-40">balance</span>
                    </div>
                    <span class="text-[10px] font-bold opacity-40 mt-1">LIFETIME: ₹{{ (rider.wallet?.lifetime_earning || 0) | number:'1.0-0' }}</span>
                  </div>
                </td>

                <!-- Actions -->
                <td class="text-right">
                  <div class="flex items-center justify-end gap-2">
                    @if ((rider.verification_status || '').toLowerCase() === 'pending') {
                      <button class="btn btn-sm btn-primary rounded-xl px-4 shadow-lg shadow-primary/10 transition-all hover:scale-105 active:scale-95" (click)="viewRiderDetail(rider)">
                        Verify Partner
                      </button>
                    } @else {
                      <button
                         class="btn btn-sm btn-ghost btn-square rounded-xl hover:bg-primary/10 hover:text-primary transition-all"
                         (click)="toggleAvailability(rider)"
                         [title]="rider.presence?.is_online ? 'Go Offline' : 'Go Online'"
                      >
                        <i class="fa-solid" [ngClass]="rider.presence?.is_online ? 'fa-toggle-on text-primary' : 'fa-toggle-off opacity-30'"></i>
                      </button>
                    }

                  </div>
                </td>
              </tr>
            }
          } @else {
            <tr>
              <td colspan="6" class="p-20 text-center">
                <div class="flex flex-col items-center gap-4 opacity-30">
                  <div class="p-8 rounded-full bg-base-200">
                    <i class="fa-solid fa-users-slash text-5xl"></i>
                  </div>
                  <div>
                    <p class="text-xl font-black">No Fleet Members Found</p>
                    <p class="text-sm font-medium">Try adjusting your filters or search query</p>
                  </div>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination Footer -->
    <div class="p-6 bg-base-200/20 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p class="text-[10px] font-black uppercase tracking-widest opacity-40">
        Showing {{ totalRiders() ? (currentPage-1)*pageSize + 1 : 0 }} - {{ Math.min(currentPage*pageSize, totalRiders()) }} of {{ totalRiders() }} records
      </p>

      <div class="join shadow-xl rounded-2xl overflow-hidden border border-white/10">
        <button
          class="btn join-item btn-sm px-6 bg-base-100/50 border-none hover:bg-primary hover:text-primary-content transition-all"
          (click)="prevPage()"
          [disabled]="currentPage === 1"
        >
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <button class="btn join-item btn-sm px-6 bg-base-100/80 border-none no-animation cursor-default font-black">
          Page {{ currentPage }}
        </button>
        <button
          class="btn join-item btn-sm px-6 bg-base-100/50 border-none hover:bg-primary hover:text-primary-content transition-all"
          (click)="nextPage()"
          [disabled]="currentPage === totalPages"
        >
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Verification Modal -->
  @if (showVerificationModal() && selectedRider()) {
    <app-rider-verification-detail
      [rider]="selectedRider()!"
      (closed)="showVerificationModal.set(false)"
      (updated)="loadRiders()"
    ></app-rider-verification-detail>
  }
</div>
